/**
 * OFFENSE / DEFENSE / SPECIAL TEAMS segmented toggle (FR-STATTRACKER-01).
 *
 * Always a segmented control, never a dropdown — the coach-facing conventions
 * in `quality/coding-conventions` forbid a dropdown for a filter on a
 * coach-facing page. Segments meet the 44 px WCAG target floor.
 *
 * This is the three-way sibling of `pages/highlight/od-toggle.tsx` (two-way,
 * offense/defense only). It lives in `components/` because both Game Summary
 * and Stat Tracker mount it.
 *
 * A11y: a real `role="tablist"` with arrow/Home/End key handling. `aria-checked`
 * is deliberately NOT set — it is invalid on `role="tab"`, and `aria-selected`
 * is the correct state attribute for a tab. Callers wire the panel they render
 * with `unitPanelId()` / `unitTabId()`.
 */
import type { StatUnit } from '../lib/api-types';

/** Wire order, and the order the segments render in. */
export const ODST_UNITS: readonly StatUnit[] = ['offense', 'defense', 'special_teams'];

export const UNIT_LABELS: Record<StatUnit, string> = {
  offense: 'OFFENSE',
  defense: 'DEFENSE',
  special_teams: 'SPECIAL TEAMS',
};

/** Phone-width and badge form. */
export const UNIT_SHORT_LABELS: Record<StatUnit, string> = {
  offense: 'OFF',
  defense: 'DEF',
  special_teams: 'ST',
};

/** Title-case form for headings and prose ("Special Teams"). */
export const UNIT_TITLES: Record<StatUnit, string> = {
  offense: 'Offense',
  defense: 'Defense',
  special_teams: 'Special Teams',
};

/**
 * Stable element ids. `scope` keeps two toggles on one page from colliding —
 * pass e.g. `'tracker'` or `'summary'`.
 */
export function unitTabId(unit: StatUnit, scope = 'odst'): string {
  return `${scope}-tab-${unit}`;
}

export function unitPanelId(unit: StatUnit, scope = 'odst'): string {
  return `${scope}-panel-${unit}`;
}

export interface OdstSegmentedToggleProps {
  value: StatUnit;
  onChange: (unit: StatUnit) => void;
  /** Restrict the segments, e.g. to hide Special Teams on a practice event. */
  units?: readonly StatUnit[];
  /** Optional subtitle per segment — a stat count makes the choice informed. */
  counts?: Partial<Record<StatUnit, number | null>>;
  /**
   * What `counts` counts, as `[singular, plural]`. Defaults to stats; the
   * Playbook page counts plays.
   */
  countNoun?: readonly [string, string];
  /** Id scope for `unitTabId` / `unitPanelId`. */
  scope?: string;
  /** Accessible name for the tablist. */
  label?: string;
  /** Dims the control while a fetch for the newly picked unit is in flight. */
  isBusy?: boolean;
}

export function OdstSegmentedToggle({
  value,
  onChange,
  units = ODST_UNITS,
  counts,
  countNoun = ['stat', 'stats'],
  scope = 'odst',
  label = 'Offense, defense or special teams',
  isBusy = false,
}: OdstSegmentedToggleProps): JSX.Element {
  function onKeyDown(keyEvent: React.KeyboardEvent<HTMLDivElement>): void {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(keyEvent.key)) return;
    keyEvent.preventDefault();
    const index = Math.max(0, units.indexOf(value));
    const step = keyEvent.key === 'ArrowRight' ? 1 : -1;
    const next =
      keyEvent.key === 'Home'
        ? 0
        : keyEvent.key === 'End'
          ? units.length - 1
          : (index + step + units.length) % units.length;
    onChange(units[next]);
  }

  return (
    <div
      className="odst-toggle"
      role="tablist"
      aria-label={label}
      aria-orientation="horizontal"
      aria-busy={isBusy}
      onKeyDown={onKeyDown}
      data-testid={`${scope}-odst-toggle`}
    >
      {units.map((unit) => {
        const isSelected = unit === value;
        const count = counts?.[unit];
        return (
          <button
            key={unit}
            type="button"
            role="tab"
            id={unitTabId(unit, scope)}
            className="odst-toggle__segment"
            aria-selected={isSelected}
            aria-controls={unitPanelId(unit, scope)}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => onChange(unit)}
            data-testid={`${scope}-odst-segment-${unit}`}
          >
            <span className="odst-toggle__label odst-toggle__label--long">{UNIT_LABELS[unit]}</span>
            <span className="odst-toggle__label odst-toggle__label--short" aria-hidden="true">
              {UNIT_SHORT_LABELS[unit]}
            </span>
            {count === undefined || count === null ? null : (
              <span className="odst-toggle__count num">
                {count} {count === 1 ? countNoun[0] : countNoun[1]}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
